import { Box, DialogContent } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postAccidentForm } from "../../../../actions/accidents";
import {
  getFormNaturalezasLesion,
  getFormZonasAfectadas
} from "../../../../actions/forms";
import { convertDateTimeToNet } from "../../../../utils/datetimeUtils";
import { getCuilCuit } from "../../../../utils/util";
import CustomDialog from "../../../commons/dialog/customDialog";
import CircularLoading from "../../../commons/loadingData/circularLoading";
import AddFormHeader from "../../accident/accidentDashboard/addDocumentation/addFormHeader";
import AccidentForm from "./accidentForm";

const AccidentFormContainer = ({
  open,
  artId,
  handleCloseForm,
  handleSubmitForm,
  facadeAccident,
  tipoDocumento,
  numeroDocumento,
  isEditMode = false,
  siniestroId = null,
  providerId = null,
}) => {
  const dispatch = useDispatch();

  const isFromValidator = Boolean(facadeAccident?.isFromValidator);

  const accidentFormState = useSelector((state) => state.accidents.form);
  const postFormState = useSelector((state) => state.accidents.postForm);
  const employers = useSelector((state) => state.forms.employers);
  const diagnosis = useSelector((state) => state.forms.diagnosis);
  const naturalezasLesion = useSelector((state) => state.forms.naturalezasLesion.data);
  const zonasAfectadas = useSelector((state) => state.forms.zonasAfectadas.data);
  const [accidentForm, setAccidentForm] = useState(null);
  const [accidentModel, setAccidentModel] = useState(null);
  const [firstTimeLoading, setFirsTimeLoading] = useState({
    zonasAfectadas: false,
    naturalezasLesion: false,
  });

  const isLoading =
    accidentFormState.isFetching ||
    !Object.keys(firstTimeLoading).every((v) => firstTimeLoading[v] === true) ||
    employers.isFetching ||
    (facadeAccident && diagnosis.isFetching) ||
    !accidentForm ||
    !accidentModel ||
    postFormState.isFetching;

  useEffect(() => {
    if (artId) {
      dispatch(getFormZonasAfectadas(artId)).finally(() => {
        setFirsTimeLoading((prevState) => ({
          ...prevState,
          zonasAfectadas: true,
        }));
      });
      dispatch(getFormNaturalezasLesion(artId)).finally(() => {
        setFirsTimeLoading((prevState) => ({
          ...prevState,
          naturalezasLesion: true,
        }));
      });
    }
  }, [artId]);

  useEffect(() => {
    if (!accidentFormState.isFetching && accidentFormState.data.model) {
      // Puede ser que vengan los campos pero estén en "null" o "" (string vacia).
      // En ese caso, no hay que pushear el objeto referente a la lista de opciones.
      const tieneDatosReferente =
        !!facadeAccident?.idReferenteExterno &&
        !!facadeAccident?.referenteApellidoyNombre &&
        !!facadeAccident?.referenteEmail;

      if (
        facadeAccident &&
        tieneDatosReferente &&
        !accidentFormState.data.model.referenteApellidoyNombre.options.some(
          (r) => r.id === facadeAccident.idReferenteExterno,
        )
      ) {
        accidentFormState.data.model.referenteApellidoyNombre.options.push({
          id: facadeAccident.idReferenteExterno,
          nombre: facadeAccident.referenteApellidoyNombre,
          email: facadeAccident.referenteEmail,
        });
      }
      setAccidentModel(accidentFormState.data.model);
    }
  }, [accidentFormState.isFetching]);

  const [employersOptions, setEmployersOptions] = useState();

  useEffect(() => {
    if (
      !accidentFormState.isFetching &&
      !employers.isFetching &&
      ((facadeAccident && !diagnosis.isFetching) || accidentFormState.data.form)
    ) {
      const form = facadeAccident || accidentFormState.data.form;
      let empleadorInicial = employers.data.find(
        (v) => v.cuit === form.cuitEmpleador,
      );
      // Empleador:
      if (!empleadorInicial) {
        empleadorInicial = {
          cuit: form?.cuitEmpleador ?? "",
          razonSocial: form?.razonSocialEmpleador ?? "",
          calle: form?.calleEmpleador ?? "",
          numero: form?.numeroEmpleador ?? "",
          piso: form?.pisoEmpleador ?? "",
          departamento: form?.departamentoEmpleador ?? "",
          localidad: form?.localidadEmpleador ?? "",
          provincia: form?.provinciaEmpleador ?? "",
          codigoPostal: form?.codigoPostalEmpleador ?? "",
          telefonos: form?.telefonosEmpleador ?? "",
        };
        setEmployersOptions([...employers.data, empleadorInicial]);
      } else {
        setEmployersOptions(employers.data);
      }

      // Diagnostico:
      const diagnosticoOms = accidentFormState.data.diagnosticoOMSConverted || form.diagnosticoOMS;
      const naturalezaLesion = form.naturalezaLesion ||
        diagnosis.data.find(
          (d) =>
            d.id ===
            (accidentFormState.data.diagnosticoOMSConverted ||
              form.diagnosticoOMS),
        )?.naturalezaLesion;
      const zonaAfectada = form.zonaAfectada ||
        diagnosis.data.find(
          (d) =>
            d.id ===
            (accidentFormState.data.diagnosticoOMSConverted ||
              form.diagnosticoOMS),
        )?.zonaAfectada;

      const agenteCausanteValue = (() => {
        const options = accidentModel?.agenteCausante?.options || [];
        const matchingOption = options.find((option) => {
          if (typeof option !== "object") {
            return option === form.agenteCausante;
          }

          const optionValue = option.id ?? option.value ?? option.codigo;
          const optionLabel = option.nombre ?? option.descripcion ?? option.label;

          return (
            optionValue === form.agenteCausante ||
            optionLabel === form.agenteCausante
          );
        });

        if (!matchingOption) {
          return form.agenteCausante || null;
        }

        return typeof matchingOption === "object"
          ? (matchingOption.id ?? matchingOption.value ?? matchingOption.codigo ?? form.agenteCausante)
          : matchingOption;
      })();


      setAccidentForm(() => ({
        ...form,
        tipoSucesoArt:
          accidentModel?.tipoSucesoArt?.options?.find(
            (e) => e.nombre == form.tipoSucesoArt?.trim(),
          )?.id || "ACCID",
        fechaHoraAbandonoTrabajo:
          form.fechaHoraAbandonoTrabajo || form.fechaHoraAbondonoTrabajo || null,
        comoOcurrioAccidente: form.comoOcurrioAccidente || "",
        agenteCausante: agenteCausanteValue,
        nroSiniestroArt: form.nroSiniestroArt || "",
        diagnosticoOMS: diagnosticoOms,
        naturalezaLesion: naturalezaLesion,
        zonaAfectada: zonaAfectada,
        tipoDocumentoTrabajador:
          form.tipoDocumentoTrabajador || tipoDocumento || "",
        numeroDocumentoTrabajador:
          form.numeroDocumentoTrabajador || numeroDocumento || "",
        apellidoTrabajador: form.apellidoTrabajador || "",
        nombreTrabajador: form.nombreTrabajador || "",
        sexoTrabajador: form.sexoTrabajador || "",
        cuilCuitTrabajador:
          form.cuilCuitTrabajador ||
          (form.numeroDocumentoTrabajador && form.sexoTrabajador
            ? getCuilCuit(form.numeroDocumentoTrabajador, form.sexoTrabajador)
            : ""),
        calleTrabajador: form.calleTrabajador || "",
        numeroTrabajador: form.numeroTrabajador || "",
        pisoTrabajador: form.pisoTrabajador || "",
        departamentoTrabajador: form.departamentoTrabajador || "",
        localidadTrabajador: form.localidadTrabajador || "",
        provinciaTrabajador: form.provinciaTrabajador || "",
        codigoPostalTrabajador: form.codigoPostalTrabajador || "",
        areaTelefonosTrabajador: form.areaTelefonosTrabajador || "",
        telefonosTrabajador: form.telefonosTrabajador || "",
        areaTelefonoCelularTrabajador: form.areaTelefonoCelularTrabajador || "",
        telefonoCelularTrabajador: form.telefonoCelularTrabajador || "",
        cuitEmpleador: empleadorInicial?.cuit ?? null,
        razonSocialEmpleador: empleadorInicial?.razonSocial ?? "",
        calleEmpleador: empleadorInicial?.calle ?? "",
        numeroEmpleador: empleadorInicial?.numero ?? "",
        pisoEmpleador: empleadorInicial?.piso ?? "",
        departamentoEmpleador: empleadorInicial?.departamento ?? "",
        localidadEmpleador: empleadorInicial?.localidad ?? "",
        provinciaEmpleador: empleadorInicial?.provincia ?? "",
        codigoPostalEmpleador: empleadorInicial?.codigoPostal ?? "",
        telefonosEmpleador: empleadorInicial?.telefonos ?? "",
      }
      ));
    }
  }, [accidentFormState.isFetching, accidentModel, employers.isFetching, diagnosis.isFetching]);

  const handleSubmit = (form) => {
    const toNullableInt = (value) => {
      if (value === "" || value === null || value === undefined) {
        return null;
      }

      return Number.isNaN(Number(value)) ? null : Number(value);
    };

    const model = {
      ...form,
      fechaHoraAccidente: convertDateTimeToNet(form.fechaHoraAccidente),
      fechaHoraAbandonoTrabajo: convertDateTimeToNet(
        form.fechaHoraAbandonoTrabajo,
      ),
      fechaNacimientoTrabajador: convertDateTimeToNet(
        form.fechaNacimientoTrabajador,
      ),
      numeroTrabajador: toNullableInt(form.numeroTrabajador),
      codigoPostalTrabajador: toNullableInt(form.codigoPostalTrabajador),
      numeroEmpleador: toNullableInt(form.numeroEmpleador),
      codigoPostalEmpleador: toNullableInt(form.codigoPostalEmpleador),
      ...(isEditMode && siniestroId ? { siniestro: siniestroId } : {}),
    };
    setAccidentForm(null);
    setAccidentModel(null);
    dispatch(postAccidentForm(model, artId, providerId))
      .then((res) => handleSubmitForm(res.value.data.ret))
      .catch(() => handleClose());
  };

  const handleClose = () => {
    setAccidentForm(null);
    setAccidentModel(null);
    handleCloseForm();
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <AddFormHeader formName={isEditMode ? "Editar Siniestro" : "siniestro"} handleClose={handleClose} />
      <DialogContent
        sx={{
          display: "flex",
          height: "100vh",
          padding: 0,
        }}>
        {isLoading ? (
          <Box
            sx={{
              width: "100%",
              marginTop: 50,
            }}>
            <CircularLoading />
          </Box>
        ) : (
          <AccidentForm
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            model={accidentModel}
            form={accidentForm}
            facadeAccident={facadeAccident}
            artId={artId}
            fromFacade={isFromValidator}
            employers={employersOptions}
            diagnosis={diagnosis.data}
            isEditMode={isEditMode}
          />
        )}
      </DialogContent>
    </CustomDialog>
  );
};

export default AccidentFormContainer;
